import {
  Card,
  CardContent,
  CardActions,
  Typography,
  Chip,
  Button,
  Box,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import type { Badge } from "./types/badges.types";

interface BadgeCardProps {
  badge: Badge;
  onEdit: (badge: Badge) => void;
  onDelete: (badgeId: string) => void;
}

export default function BadgeCard({ badge, onEdit, onDelete }: BadgeCardProps) {
  return (
    <Card
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        borderRadius: 3,
        boxShadow: "none",
        transition: "box-shadow 0.2s ease",
        "&:hover": {
          boxShadow: "0 6px 20px rgba(0,0,0,0.08)",
        },
      }}
    >
      <CardContent sx={{ flexGrow: 1, p: 3 }}>
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            mb: 2,
          }}
        >
          <Box sx={{ fontSize: "3rem", lineHeight: 1 }}>{badge.icon}</Box>

          {badge.issueRule === "auto" && (
            <Chip
              label="Auto-issue"
              size="small"
              sx={{
                backgroundColor: "#dcfce7",
                color: "#166534",
                fontWeight: 500,
                fontSize: "0.75rem",
                height: 26,
                borderRadius: "999px",
              }}
            />
          )}
        </Box>

        {/* Title */}
        <Typography
          sx={{
            fontSize: "1.25rem",
            fontWeight: 600,
            mb: 0.5,
          }}
        >
          {badge.name}
        </Typography>

        {/* Description */}
        <Typography
          sx={{
            fontSize: "0.95rem",
            color: "text.secondary",
            mb: 2,
          }}
        >
          {badge.description}
        </Typography>

        {/* Condition box */}
        <Box
          sx={{
            backgroundColor: "#f9fafb",
            borderRadius: 2,
            px: 2,
            py: 1.5,
          }}
        >
          <Typography
            sx={{
              fontSize: "0.875rem",
              color: "text.secondary",
            }}
          >
            Condition: {badge.condition}
          </Typography>
        </Box>
      </CardContent>

      {/* Actions */}
      <CardActions sx={{ px: 3, pb: 3, pt: 0, gap: 1 }}>
        <Button
          variant="outlined"
          startIcon={<EditIcon />}
          onClick={() => onEdit(badge)}
          sx={{
            textTransform: "none",
            fontWeight: 500,
            borderColor: "#d1d5db",
            color: "text.primary",
            borderRadius: 2,
            "&:hover": {
              backgroundColor: "#f9fafb",
              borderColor: "#9ca3af",
            },
          }}
        >
          Edit
        </Button>

        <Button
          variant="outlined"
          startIcon={<DeleteIcon />}
          onClick={() => onDelete(badge.id)}
          sx={{
            textTransform: "none",
            fontWeight: 500,
            borderColor: "#fca5a5",
            color: "#dc2626",
            borderRadius: 2,
            "&:hover": {
              backgroundColor: "#fee2e2",
              borderColor: "#f87171",
            },
          }}
        >
          Delete
        </Button>
      </CardActions>
    </Card>
  );
}
